export interface ProjectConfig {
  projectName: string;
  hostName?: string;
  frontendUrl?: string;
  backendUri?: string;
  portBase?: number;
  nodeEnv?: string;
  enablePlayground?: boolean;
  deploymentName: DeploymentName;
  adminEmails?: string[];
  adminUsers?: AdminUser[];
}

// values must be lowercase
export enum DeploymentName {
  /**
   * production is the main environment. it's used for the public-facing website and the production backend
   */
  PRODUCTION = "production",
  /**
   * staging is usually close to the production environment. it's used for testing before a new version is deployed to production
   */
  STAGING = "staging",
  /**
   * sandbox is like a playground for testing. usually it's auto-deployed on every commit so expect it to be unstable
   */
  SANDBOX = "sandbox",
  /**
   * preview is more stable than sandbox and is meant to be used by the commissioner for testing. it's usually deployed manually
   */
  PREVIEW = "preview",
  /**
   * the local development environment.
   */
  DEV = "dev",
}

export type AdminUser = {
  email: string;
  givenName: string;
  familyName: string;
};

export enum Targets {
  BACKEND = "backend",
  FRONTEND = "frontend",
  DEV = "dev",
  DB = "db",
}

export interface CorsConfig {
  allowMethods?: string[];
  allowHeaders?: string[];
  allowOriginList?: string[];
}

export interface ProxyConfig {
  port: string;
  cors?: CorsConfig;
}

export interface ServiceConfig {
  build?: {
    dockerfile?: string;
  };
  name?: string;
  command?: string[];
  image?: string;
  ports?: string[];
  volumes?: string[];
  backend_dependency?: boolean;
  networks?: string[];
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  labels?: Record<string, any>;
  entrypoint?: string;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  depends_on?: Record<string, any>;
  configs?: {
    source: string;
    target: string;
    uid?: string;
    mode?: string;
  }[];
  expose?: string[];
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  healthcheck?: Record<string, any>;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  environment?: Record<string, any>;
  proxy?: Record<string, ProxyConfig>;
  restart?: string;
  env_file?: string[];
}

export interface DockerComposeObject {
  version?: string;
  services: Record<string, ServiceConfig>;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  volumes: Record<string, any>;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  networks?: Record<string, any>;
  restart?: string;
}

export type DockerComposeTransformer = (
  dockerComposeObj: DockerComposeObject,
  projectConfig: ProjectConfig,
) => void;

export interface ModuleConfig {
  prefix?: string;
  targets: TargetConfigs;
  additionalFiles?: {
    path: string;
    content: string | object;
  }[];
  services?: Record<string, ServiceConfig>;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  volumes?: Record<string, any>;
  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  networks?: Record<string, any>;
  configs?: Record<
    string,
    {
      content?: string;
      file?: string;
      external?: boolean;
      name?: string;
    }
  >;
}
export type ModuleConfigGenerator = (config: ProjectConfig) => ModuleConfig;
export type TargetConfigs = Partial<
  Record<Targets | string, Record<string, string>>
>;

export type WithRequiredKeys<T, K extends keyof T> = Partial<Omit<T, K>> &
  Required<Pick<T, K>>;

export type AutocompleteModuleConfig<
  ModuleOptionsType,
  RequiredKeys extends keyof ModuleOptionsType,
> = (
  projectSpecificModuleOptions: WithRequiredKeys<
    ModuleOptionsType,
    RequiredKeys
  >,
) => ModuleConfigGenerator;

export type InstanceConfig<
  ProjectConfigType extends ProjectConfig = ProjectConfig,
> = {
  project: ProjectConfigType;
  projectSpecific?: TargetConfigs;
  modules: ModuleConfigGenerator[];
  transformers?: DockerComposeTransformer[];
};

export function generateDefaultConfig(): ModuleConfigGenerator {
  return (config: ProjectConfig) => {
    const nodeEnv = config.nodeEnv ?? "development";
    const portBase = config.portBase;
    const frontendPort = portBase ? `${portBase}80` : "80";
    const backendPort = portBase ? `${portBase}30` : "3000";
    const backendUri = config.backendUri ?? `http://127.0.0.1:${backendPort}`;
    const frontendUri =
      config.frontendUrl ?? `http://127.0.0.1:${frontendPort}`;

    const projectName = config.projectName;
    const deploymentName = config.deploymentName;

    const moduleConfig: ModuleConfig = {
      targets: {
        [Targets.BACKEND]: {
          nodeEnv,
          backendPort,
          adminEmails: config.adminEmails?.join(",") ?? "",
          adminUsers: JSON.stringify(config.adminUsers),
          deploymentName,
          frontendUri,
          projectName,
        },
        [Targets.FRONTEND]: {
          frontendPort,
          backendUri,
          deploymentName,
          projectName,
        },
        [Targets.DEV]: {
          backendPort,
          frontendPort,
          deploymentName,
          backendUri,
          nodeEnv,
          projectName,
        },
      },
    };

    return moduleConfig;
  };
}
